import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { LIVE, startupReturn, tunnelState, watchTunnel } from "@/lib/tunnel-link";
import { GUEST, markeerMislukt, readAppointmentFor, wasMislukt, wisMislukt } from "@/lib/rendezvous";
import { LOCAL, whoIsSignedIn } from "@/lib/session-mode";


const AuthContext = createContext(null);

/**
 * Zo lang wacht het opstarten op de weg terug naar de Mac, en daarna komt het
 * QR-scherm terwijl de poging op de achtergrond doorgaat. Opstart-fix 12 oktober
 * 2026 (besluit Frank): hooguit 5 s; hier stond 25 s, en op glas werden dat twee
 * koude starts van ongeveer 30 s zonder iets op het scherm.
 */
export const STARTUP_WAIT_MS = 5000;

/**
 * GLASFOUT 7, v0.9.159 (15 oktober 2026): moet de app opnieuw vragen wie er is?
 * PUUR. Ja zodra het kanaal naar de Mac LIVE wordt, langs welke weg ook.
 *
 * Tot deze dag gebeurde dat alleen als juist de terugweg via de bewaarde
 * afspraak slaagde. Na een mislukte poging staat de opstartwacht op 0 s, dus
 * `/api/auth/me` ging over gewoon HTTP naar connect (404) voordat er een kanaal
 * was, en een scan die daarna WEL een kanaal gaf, bleef op het inlogscherm
 * staan: niemand vroeg het nog een keer.
 */
export function moetOpnieuwVragen(vorige, nu) {
  return nu === LIVE && vorige !== LIVE;
}

/**
 * Hoe lang deze start wacht. PUUR. Mislukte de vorige poging: niet, TENZIJ de
 * telefoon een afspraak met de Mac bewaard heeft. Dan wacht hij altijd (besluit
 * Frank 15 oktober 2026, fix wie-ben-ik): de terugweg komt op een gezonde
 * verbinding binnen een kwart seconde, en zonder die wacht vroeg de telefoon wie
 * hij was voordat het kanaal er was.
 */
export function opstartWachttijd(vorigeMislukt, heeftAfspraak = false) {
  if (heeftAfspraak) return STARTUP_WAIT_MS;
  return vorigeMislukt ? 0 : STARTUP_WAIT_MS;
}

/**
 * FIX WIE-BEN-IK (15 oktober 2026): mag de app nu vragen wie er is? PUUR.
 *
 * Nee voor een telefoon die een afspraak met de Mac bewaard heeft zolang het
 * kanaal er nog niet is. Zo'n vraag ging over gewoon HTTP naar connect, kreeg
 * 404, en dat telde als "het token houdt niet meer". Gemeten op Franks iPhone:
 * `/api/auth/me` 404 18 ms voor het kanaal klaar was (18:03:05.238), en het
 * inlogscherm bleef staan. Zodra het kanaal live is, vraagt de app alsnog
 * (`moetOpnieuwVragen`). Op de Mac staat geen afspraak van de kant guest, dus
 * daar verandert niets.
 */
export function magNuVragen({ tunnelLive, heeftAfspraak }) {
  return Boolean(tunnelLive) || !heeftAfspraak;
}


// Het token wordt op MODULENIVEAU aan elk verzoek gehangen, en niet in een
// effect van AuthProvider. Dat is de reparatie van punt 17, en de oorzaak zat
// in de volgorde waarin React effecten draait.
//
// React draait effecten van KIND naar OUDER. `BrandingProvider` staat in
// App.js binnen `AuthProvider`, dus zijn effect vuurt EERDER. Stond de
// interceptor in het effect van AuthProvider, dan vertrok het brandingverzoek
// bij elke herlading zonder Authorization-kop en kwam er een 401 terug, vlak
// voordat `/api/auth/me` netjes 200 gaf.
//
// Het is bewust geen tweede maatregel in BrandingContext ("wacht op een
// sessie"), want dan blijft de fout bestaan voor elk volgend onderdeel dat
// eerder dan AuthProvider vuurt. Dit is een eigenschap van axios en niet van
// een component, dus zij hoort hier: bij het laden van de module, voordat er
// ook maar iets is gerenderd.
//
// Alleen het VERZOEK verhuist. De antwoord-interceptor leunt op `endSession`
// en `refreshOnce`, die aan de component hangen, en blijft waar hij stond.
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const interceptorSet = useRef(false);
  // Een lopende verversing die alle wachtende verzoeken delen. Zonder dit doet
  // elk gelijktijdig verzoek zijn eigen refresh: vier schermen die tegelijk
  // 401 krijgen mintten vier tokenparen waarvan de laatste won.
  const refreshInFlight = useRef(null);
  // De melding hoort een keer te komen, niet een keer per mislukt verzoek.
  const sessionEnded = useRef(false);
  // Fix wie-ben-ik: het volgnummer van de laatste vraag wie er is.
  const vraagNummer = useRef(0);

  const getAccessToken = useCallback(() => {
    return localStorage.getItem("access_token");
  }, []);

  const getRefreshToken = useCallback(() => {
    return localStorage.getItem("refresh_token");
  }, []);

  const setTokens = useCallback((access, refresh) => {
    if (access) localStorage.setItem("access_token", access);
    if (refresh) localStorage.setItem("refresh_token", refresh);
    // Een verse sessie mag opnieuw aflopen, en dan hoort de melding er weer te
    // zijn.
    sessionEnded.current = false;
  }, []);

  const clearTokens = useCallback(() => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
  }, []);

  /**
   * De sessie is voorbij, en dat wordt gezegd.
   *
   * Wat hier stond was drie soorten stilte. Zonder refresh token deed de
   * interceptor niets, dus de 401 viel door naar het scherm, en achttien
   * plekken in deze app eten een 401 op met `.catch(() => {})`. Mislukte het
   * verversen wel, dan werd er opgeruimd zonder een woord. Het resultaat was
   * een app die er normaal uitzag en niets laadde, op 6 augustus veertien
   * schermen achter elkaar.
   *
   * Een keer, niet een keer per mislukt verzoek: vier gelijktijdige schermen
   * horen niet vier meldingen op te leveren. `setUser(null)` doet de rest,
   * want RequireSession stuurt dan naar het inlogscherm.
   */
  const endSession = useCallback(() => {
    if (sessionEnded.current) return;
    sessionEnded.current = true;
    clearTokens();
    setUser(null);
    toast.error("Je sessie is verlopen. Log opnieuw in.");
  }, [clearTokens]);

  /**
   * Verversen, hoogstens een keer tegelijk.
   *
   * Iedereen die tegelijk tegen een 401 aanloopt krijgt dezelfde belofte terug.
   */
  const refreshOnce = useCallback(() => {
    if (!refreshInFlight.current) {
      const refreshToken = localStorage.getItem("refresh_token");
      if (!refreshToken) return Promise.reject(new Error("no refresh token"));
      refreshInFlight.current = axios
        .post(`${API}/auth/refresh`, { refresh_token: refreshToken })
        .then((response) => {
          const { access_token, refresh_token } = response.data;
          setTokens(access_token, refresh_token);
          return access_token;
        })
        .finally(() => {
          refreshInFlight.current = null;
        });
    }
    return refreshInFlight.current;
  }, [setTokens]);

  /**
   * Who is there.
   *
   * A token is asked about first, because a phone that scanned a code holds
   * one. Without a token the question goes to `whoIsSignedIn`, and the answer
   * it gives for a phone is nobody: what used to happen here was a made-up
   * "Local User" in both the try and the catch, which is true on a Mac
   * running its own copy and a lie everywhere else. It does not look like an
   * error either. It looks like a working app whose every call returns 401,
   * and it took an hour to see through on 2026-07-26.
   */
  const fetchUser = useCallback(async () => {
    // Fix wie-ben-ik: alleen het antwoord op de NIEUWSTE vraag mag de gebruiker
    // zetten. Twee vragen tegelijk (een over HTTP, een over het kanaal) gaven
    // een race: kwam het 404 als laatste binnen, dan was je weer niemand.
    const volg = ++vraagNummer.current;
    const nieuwste = () => volg === vraagNummer.current;
    if (!magNuVragen({ tunnelLive: tunnelState()?.where === LIVE, heeftAfspraak: !!readAppointmentFor(GUEST) })) {
      return false;
    }
    const token = getAccessToken();

    if (token) {
      try {
        const resp = await axios.get(`${API}/auth/me`);
        if (nieuwste()) setUser(resp.data);
        return true;
      } catch {
        // The token is there and did not hold. That is not a local session,
        // it is an expired one, and the way out of it is the sign-in screen.
        if (nieuwste()) setUser(null);
        return false;
      }
    }

    let backend = null;
    if (tunnelState().where !== LIVE) {
      try {
        const resp = await axios.get(`${API}/mode`);
        backend = resp.data;
      } catch {
        // No backend of our own. A phone that has loaded the app and reached
        // nothing yet: nobody, and it needs to see that.
        backend = null;
      }
    }

    const who = whoIsSignedIn({
      hasToken: false,
      tunnelLive: tunnelState().where === LIVE,
      backend,
    });

    if (!nieuwste()) return who === LOCAL;
    if (who === LOCAL) {
      setUser({ plan: "free", display_name: "Local User" });
      return true;
    }
    setUser(null);
    return false;
  }, [getAccessToken]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setTokens(response.data.access_token, response.data.refresh_token);
    await fetchUser();
    return response.data;
  }, [fetchUser, setTokens]);

  const register = useCallback(async (email, password, display_name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, display_name });
    setTokens(response.data.access_token, response.data.refresh_token);
    await fetchUser();
    return response.data;
  }, [fetchUser, setTokens]);

  const googleLogin = useCallback(async (idToken) => {
    const response = await axios.post(`${API}/auth/google`, { id_token: idToken });
    setTokens(response.data.access_token, response.data.refresh_token);
    await fetchUser();
    return response.data;
  }, [fetchUser, setTokens]);

  const logout = useCallback(() => {
    clearTokens();
    setUser(null);
  }, [clearTokens]);

  useEffect(() => {
    if (interceptorSet.current) return;
    interceptorSet.current = true;

    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        // De verversing gaat over dezelfde instantie en dus door deze
        // interceptor heen. Zonder deze uitzondering ving hij zijn EIGEN
        // refresh-aanroep op, zag een verse config zonder `_retry`, en
        // probeerde opnieuw. Gemeten op 6 augustus 2026: vier datacalls
        // werden 204 aanroepen naar `/auth/refresh` en veertien werden er
        // 214, allebei alleen zo laag door een noodrem in de meting.
        const isRefreshCall = (originalRequest?.url || "").includes("/auth/refresh");

        if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isRefreshCall) {
          originalRequest._retry = true;
          if (!localStorage.getItem("refresh_token")) {
            endSession();
            return Promise.reject(error);
          }
          try {
            const access = await refreshOnce();
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${access}`;
            return axios(originalRequest);
          } catch {
            endSession();
          }
        }
        return Promise.reject(error);
      }
    );
  }, [endSession, refreshOnce]);

  // Glasfout 7: elk kanaal dat live wordt, ook een via een scan, laat de app
  // opnieuw vragen wie er is, en wist de onthouden mislukking.
  useEffect(() => {
    let vorige = tunnelState()?.where;
    return watchTunnel((stand) => {
      const nu = stand?.where;
      if (moetOpnieuwVragen(vorige, nu)) {
        wisMislukt();
        fetchUser();
      }
      vorige = nu;
    });
  }, [fetchUser]);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      // PUNT 1, 12 OKTOBER 2026: een telefoon die net ververste heeft zijn
      // tokens nog, maar nog geen kanaal naar de Mac. Wie dan meteen vraagt wie
      // er is, krijgt "niemand" van een server zonder api en staat op het
      // inlogscherm terwijl de weg terug al loopt. Dus eerst de eerste poging
      // afwachten, met een grens; op de Mac is er geen afspraak en is dit meteen
      // klaar.
      const terug = Promise.resolve(startupReturn());
      const TE_LAAT = "te laat";
      const uitkomst = await Promise.race([
        terug, new Promise((r) => setTimeout(() => r(TE_LAAT), opstartWachttijd(wasMislukt(), !!readAppointmentFor(GUEST))))]);
      // Opstart-fix: geen kanaal binnen de grens, dan onthouden, zodat de
      // volgende koude start niet opnieuw wacht.
      if (uitkomst === TE_LAAT) markeerMislukt();
      await fetchUser();
      setIsLoading(false);
      // Komt het kanaal pas na de grens terug, dan alsnog vragen wie er is.
      terug.then((gelukt) => {
        if (gelukt) {
          wisMislukt();
          fetchUser();
        }
      });
    };
    init();
  }, [fetchUser]);

  return (
    <AuthContext.Provider value={{ user, isLoading, login, register, googleLogin, logout, getAccessToken, refreshUser: fetchUser, isAuthenticated: !!user?.email }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * F1c: het id van wie er is ingelogd, of null. Gooit NIET buiten de provider,
 * want de onderste balk wordt ook los getekend. Een teller die per lid telt,
 * hangt hieraan: wisselt het lid, dan begint de telling opnieuw.
 */
export function useLidId() {
  return useContext(AuthContext)?.user?.id ?? null;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
